/**
 * Build identity injected at vite build time via define{} in vite.config.ts.
 * The commit SHA in dist/index.html's meta tag is rewritten post-build by
 * scripts/inject-sha.mjs to capture HEAD at the moment of build (not the
 * value baked at vite-config eval time).
 */
export const BUILD_INFO = {
  commitSha:
    typeof __SWIMSET_COMMIT_SHA__ !== "undefined" ? __SWIMSET_COMMIT_SHA__ : "unknown",
  buildTime:
    typeof __SWIMSET_BUILD_TIME__ !== "undefined" ? __SWIMSET_BUILD_TIME__ : "unknown",
};

/**
 * Classifies where THIS page load came from, given the page's own href.
 * Takes the href as a parameter (not read from `location` internally) so it
 * is unit-testable without a DOM — callers pass `location.href`.
 *
 * NOT THE WHOLE STORY ANY MORE (2026-07-13) — use `buildSourceLabel` below for
 * anything the owner reads. Since the shell gained an over-the-air bundle cache,
 * a `file://` page can be EITHER the copy compiled into the app (possibly days
 * old) OR a NEWER build the phone downloaded and kept. This function cannot tell
 * them apart, because the URL cannot: both are `file://`. Only the native side
 * knows, and it says so in the handshake.
 *
 * Three cases, matching the iOS shell's own vocabulary
 * (WebViewSource.swift: `.remote` / `.localhost` / `.bundled`):
 *
 * - "live" — the real https://swimset.club origin (WebViewSource `.remote`).
 * - "offline copy" — the iOS shell's BUNDLED fallback, painted from a
 *   file:// URL when swimset.club couldn't be reached at launch
 *   (WebViewSource `.bundled`). This is the case the owner needs to SEE —
 *   it means he is looking at whatever build shipped with the last Xcode
 *   install, not today's deploy. Any other unrecognized origin also falls
 *   here: better to say "not live" than to guess at a fourth state.
 * - "dev" — a localhost / 127.0.0.1 origin: `pnpm dev`'s Vite server, or the
 *   iOS shell's DebugView "localhost" override (WebViewSource `.localhost`).
 *   This is a developer pointed at a live dev server, NOT the shipped
 *   offline copy, so it must never be lumped in with "offline copy".
 */
export function webLoadSource(href: string): "live" | "offline copy" | "dev" {
  let url: URL;
  try {
    url = new URL(href);
  } catch {
    return "offline copy";
  }
  if (url.protocol === "file:") return "offline copy";
  if (url.hostname === "localhost" || url.hostname === "127.0.0.1") return "dev";
  if (url.protocol === "https:" && url.hostname === "swimset.club") return "live";
  return "offline copy";
}

/**
 * What the iOS shell told us it painted, from the handshake. Absent on a browser
 * (no shell) and on any shell older than 2026-07-13 (the fields are additive).
 */
export interface NativeWebBuild {
  origin?: "bundled" | "cached" | "remote" | "localhost" | null;
  /** true = this IS the build swimset.club last served. false = a newer one exists.
   *  null/absent = the phone has never reached the site and does not know. */
  isLatest?: boolean | null;
}

/**
 * THE HONEST ANSWER to "which build am I looking at", for the Save-health line.
 *
 * The URL alone is not enough. A page painted from the downloaded bundle cache is a
 * `file://` load exactly like the copy compiled into the app — so classifying by URL
 * would print "offline copy" while actually showing a build NEWER than the one shipped
 * in the binary. That is a lie, and lying about which build is running is precisely the
 * failure that had the owner debugging ghosts on 2026-07-13 (his phone had been silently
 * running a four-day-old build).
 *
 * So the native shell tells us, and we say what it says:
 *
 * - "live"         — running straight from swimset.club.
 * - "latest"       — a local copy, and it is the build the site last served. Nothing newer
 *                    exists as far as this phone knows.
 * - "offline copy" — a local copy that may be behind the site. Includes "we have never
 *                    reached the site, so we do not know" — an unknown must NEVER render as
 *                    "latest".
 * - "dev"          — a local dev server.
 *
 * Plain words on purpose (no jargon, no colour-only signal): the owner reads this line.
 */
export function buildSourceLabel(
  href: string,
  native?: NativeWebBuild | null,
): "live" | "latest" | "offline copy" | "dev" {
  const fromUrl = webLoadSource(href);
  // "live" and "dev" are already unambiguous from the URL — a file:// page is the only
  // case the URL cannot resolve.
  if (fromUrl !== "offline copy") return fromUrl;
  return native?.isLatest === true ? "latest" : "offline copy";
}
